import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { api, startDirectCheckout } from "@/lib/api";
import { fmtCAD } from "@/lib/format";
import { ArrowRight, AirplaneTakeoff, Heart, Check, MapPin, Receipt, Sparkle } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * Landing V3 — Mission-first variant.
 * Hero: leads with "Your medical receipts can fly a Canadian to chemo."
 * Hook: emotional + mission-driven.
 */
export default function LandingV3() {
  const navigate = useNavigate();
  const { isAuthed, loading } = useAuth();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    if (!loading && isAuthed) navigate("/log");
  }, [isAuthed, loading, navigate]);

  useEffect(() => {
    api.get("/billing/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  const goSubscribe = async (plan = "yearly") => {
    try { await startDirectCheckout(plan); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not start checkout. Try again in a moment."); }
  };

  return (
    <div className="min-h-screen relative bg-[#F7F6F2]">
      {/* Background warm gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#FFF7EE] via-[#F7F6F2] to-[#EBE9E0] pointer-events-none" />

      <div className="relative z-10 min-h-screen flex flex-col">
        {/* Top bar */}
        <header className="px-6 sm:px-10 py-5 flex items-center justify-between">
          <div className="bg-white rounded-xl px-2 py-1 shadow-sm">
            <img src="/medeasy-logo.png" alt="MedEasy" className="h-10 sm:h-12 w-auto object-contain" />
          </div>
          <Button
            data-testid="v3-header-signin"
            onClick={() => navigate("/login")}
            className="rounded-full bg-[#2C4C3B] hover:bg-[#3A634D] text-white"
          >
            Sign in
          </Button>
        </header>

        <main className="flex-1 px-6 sm:px-10 lg:px-16 pt-8 sm:pt-16 pb-16">
          <div className="max-w-5xl mx-auto">
            {/* Mission pill */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#C17767]/10 border border-[#C17767]/20 text-[#C17767] text-xs uppercase tracking-[0.18em] font-bold mb-8">
              <Heart weight="fill" size={12} /> A small Canadian app with a mission
            </div>

            {/* Headline */}
            <h1 className="font-[Manrope] text-4xl sm:text-6xl lg:text-7xl font-bold tracking-tight leading-[1.0] text-[#1E2420]">
              Too tired to track<br />
              medical expenses?<br />
              <span className="text-[#C17767]">So was I.</span>
            </h1>

            <p className="text-[#3D4540] mt-8 text-lg sm:text-xl leading-relaxed max-w-2xl">
              MedEasy is a CRA medical-expense tracker for Canadians living with chronic illness. Every $49.50/year subscription sets aside <strong>10% for Hope Air</strong> — the charity that arranges <strong>free flights, ground travel, and lodging</strong> for Canadians who can&apos;t afford the trip to chemo, surgery, or a specialist. <strong>Your receipts can fly a Canadian to their appointment.</strong>
            </p>

            {/* Hope Air community card */}
            <div className="mt-10 rounded-3xl bg-white border border-[#E3E1D9] shadow-lg shadow-[#C17767]/5 p-6 sm:p-8 max-w-3xl">
              <div className="flex items-start gap-5 flex-wrap">
                <div className="w-14 h-14 rounded-2xl bg-[#C17767]/10 flex items-center justify-center shrink-0">
                  <AirplaneTakeoff weight="duotone" size={28} className="text-[#C17767]" />
                </div>
                <div className="flex-1 min-w-[220px]">
                  <div className="text-[10px] uppercase tracking-[0.25em] text-[#5C6660] font-semibold">Our pledge to Canadians</div>
                  <div className="font-[Manrope] font-bold text-3xl sm:text-4xl text-[#C17767] mt-1">
                    10% of every subscription
                  </div>
                  <div className="text-sm text-[#5C6660] mt-2">
                    set aside for Hope Air to help fly Canadian patients to medical care.{" "}
                    <button onClick={() => navigate("/hope-air")} className="text-[#2C4C3B] underline font-medium" data-testid="v3-hope-link">
                      Why Hope Air →
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* CTA row */}
            <div className="mt-10 flex flex-wrap gap-3 items-center">
              <Button
                data-testid="v3-cta-primary"
                onClick={goSubscribe}
                size="lg"
                className="rounded-full h-14 px-8 bg-[#C17767] hover:bg-[#A45D4F] text-white text-base font-semibold"
              >
                Join — $49.50 / year
                <ArrowRight size={18} className="ml-2" weight="bold" />
              </Button>
              <div className="text-sm text-[#5C6660]">
                <div className="font-medium text-[#1E2420]">Cancel anytime</div>
                <div>10% of every payment → Hope Air.</div>
              </div>
            </div>

            {/* Trust strip */}
            <div className="mt-7 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs sm:text-sm text-[#5C6660]">
              <span className="inline-flex items-center gap-1.5"><span aria-hidden>🇨🇦</span> Built in Canada</span>
              <span className="text-[#E3E1D9]">·</span>
              <span className="inline-flex items-center gap-1.5"><Sparkle weight="fill" size={12} className="text-[#2E7BC4]" /> AI receipt scanning</span>
              <span className="text-[#E3E1D9]">·</span>
              <span className="inline-flex items-center gap-1.5"><Receipt weight="fill" size={12} className="text-[#3FA85E]" /> CRA-ready PDF</span>
            </div>

            {/* What it does */}
            <div className="mt-20 sm:mt-28 grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
              {[
                { Icon: MapPin, label: "Every kilometre", text: "Auto-calculates round-trip mileage for CRA. Province-aware rates." },
                { Icon: Receipt, label: "Every receipt", text: "Snap a parking stub or prescription. AI fills in the date, amount, and DIN." },
                { Icon: AirplaneTakeoff, label: "Every dollar", text: "10% of every subscription goes to Hope Air to help another Canadian get to care." },
              ].map(({ Icon, label, text }) => (
                <div key={label} className="rounded-2xl bg-white border border-[#E3E1D9] p-6">
                  <Icon weight="duotone" size={28} className="text-[#2C4C3B]" />
                  <div className="font-[Manrope] font-semibold text-lg mt-3">{label}</div>
                  <p className="text-sm text-[#5C6660] mt-1.5 leading-relaxed">{text}</p>
                </div>
              ))}
            </div>

            {/* Founder's story */}
            <div className="mt-20 sm:mt-24 max-w-3xl" data-testid="v3-founders-note">
              <div className="text-[10px] uppercase tracking-[0.28em] text-[#5C6660] font-semibold mb-5">
                A note from the founder
              </div>
              <div className="flex items-start gap-5">
                <div className="shrink-0 w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-[#2C4C3B] text-white flex items-center justify-center font-[Manrope] font-bold text-xl sm:text-2xl shadow-sm">
                  M
                </div>
                <blockquote className="flex-1">
                  <p className="font-[Manrope] text-2xl sm:text-3xl leading-[1.25] tracking-tight text-[#1E2420]">
                    <span className="text-[#C17767] font-bold">&ldquo;</span>
                    Being sick, injured, or living with a chronic condition is hard enough.
                  </p>
                  <p className="font-[Manrope] text-2xl sm:text-3xl leading-[1.25] tracking-tight text-[#1E2420] mt-3">
                    Keeping track of the expenses shouldn&apos;t be.
                  </p>
                  <p className="font-[Manrope] text-lg sm:text-xl leading-[1.45] tracking-tight text-[#3D4540] mt-5">
                    After discovering I was leaving thousands of dollars in medical deductions unclaimed, I built MedEasy to make tracking receipts, prescriptions, and medical travel effortless.
                  </p>
                  <p className="font-[Manrope] text-2xl sm:text-3xl leading-[1.25] tracking-tight text-[#1E2420] mt-5">
                    <span className="text-[#2C4C3B] font-semibold">One photo. CRA-ready.</span>
                    <span className="text-[#C17767] font-bold">&rdquo;</span>
                  </p>
                  <footer className="mt-6 text-sm text-[#5C6660]">— The founder, MedEasy</footer>
                </blockquote>
              </div>
            </div>

            {/* Why Hope Air — story block */}
            <div className="mt-16 rounded-3xl bg-[#1E2420] text-white p-8 sm:p-12">
              <div className="text-[10px] uppercase tracking-[0.25em] text-white/60 font-semibold mb-3">Why Hope Air</div>
              <h2 className="font-[Manrope] font-bold text-2xl sm:text-3xl leading-tight">
                For 180,000+ Canadians, the medical travel <em className="text-[#D4A373] not-italic">is</em> the barrier.
              </h2>
              <p className="text-white/80 mt-5 leading-relaxed max-w-2xl">
                When you live in northern Ontario, rural BC, or a small town in the Maritimes, the nearest oncologist might be a province away. Most Canadians can absorb the travel cost. <strong>Some can&apos;t.</strong> Hope Air arranges <strong>free flights, ground travel, hotel stays, and meal vouchers</strong> so they can make the appointment — the same kinds of expenses MedEasy helps you track.
              </p>
              <p className="text-white/80 mt-4 leading-relaxed max-w-2xl">
                Every MedEasy subscription sets aside <strong className="text-[#D4A373]">10%</strong> for them. The more we grow, the more flights happen. That&apos;s it. That&apos;s the deal.
              </p>
              <Button
                data-testid="v3-hope-air-cta"
                onClick={() => navigate("/hope-air")}
                variant="outline"
                className="mt-6 rounded-full bg-white/10 hover:bg-white/20 text-white border-white/20"
              >
                Read the full story <ArrowRight size={16} className="ml-1.5" />
              </Button>
            </div>

            {/* Final CTA */}
            <div className="mt-16 text-center">
              <h2 className="font-[Manrope] text-3xl sm:text-4xl font-bold tracking-tight">
                Track yours. Help theirs.
              </h2>
              <p className="text-[#5C6660] mt-3 max-w-xl mx-auto">
                $49.50 CAD per year. 10% to Hope Air, every single payment.
              </p>
              <Button
                data-testid="v3-cta-final"
                onClick={goSubscribe}
                size="lg"
                className="mt-8 rounded-full h-14 px-10 bg-[#C17767] hover:bg-[#A45D4F] text-white text-base font-semibold"
              >
                Join — $49.50 / year
                <ArrowRight size={18} className="ml-2" weight="bold" />
              </Button>
              <div className="mt-4 flex flex-wrap items-center justify-center gap-x-5 gap-y-1 text-xs text-[#5C6660]">
                <span className="inline-flex items-center gap-1"><Check size={12} weight="bold" className="text-[#3FA85E]" /> Unlimited entries</span>
                <span className="inline-flex items-center gap-1"><Check size={12} weight="bold" className="text-[#3FA85E]" /> AI receipt scan</span>
                <span className="inline-flex items-center gap-1"><Check size={12} weight="bold" className="text-[#3FA85E]" /> CRA-ready PDF</span>
                <span className="inline-flex items-center gap-1"><Heart weight="fill" size={12} className="text-[#C17767]" /> 10% to Hope Air</span>
              </div>
            </div>
          </div>
        </main>

        <footer className="border-t border-[#E3E1D9] px-6 sm:px-10 py-6 text-center text-xs text-[#5C6660] space-y-2">
          <div>MedEasy · Built in Canada 🇨🇦 · 10% of every payment supports <button onClick={() => navigate("/hope-air")} className="underline hover:text-[#2C4C3B]">Hope Air</button></div>
          <div className="flex items-center justify-center gap-4">
            <button onClick={() => navigate("/terms")} className="hover:text-[#1E2420] underline" data-testid="v3-footer-terms">Terms</button>
            <span className="text-[#E3E1D9]">·</span>
            <button onClick={() => navigate("/privacy")} className="hover:text-[#1E2420] underline" data-testid="v3-footer-privacy">Privacy</button>
          </div>
        </footer>
      </div>
    </div>
  );
}
